import { useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { TelestratorCanvas } from '@/features/canvas/TelestratorCanvas';
import { useCanvasEnvelope } from '@/features/canvas/useCanvasEnvelope';
import type { DraftContext } from '@/features/coach-review/composerDrafts';
import { ClipTrack } from '@/features/coach-review/components/ClipTrack';
import { FrameStepper } from '@/features/coach-review/components/FrameStepper';
import { formatEditorTime, type ClipWindow } from '@/features/coach-review/clipTrack';
import { SLOT_LABELS } from '@/features/matches/matchSlots';
import { formatPlayhead, useVideoPlayhead } from '@/features/playback/useVideoPlayhead';
import type { DrawingV2 } from '@/lib/canvas/telestrator-schema';
import type { ResourcePin } from '@/lib/types';
import type { ResourceVideoSession, ResourceVideoValue } from './resourceVideoSession';
import { shortcutBlocked } from './resourceShortcuts';
import { resourceVideoSourceKey, type ResourceVideoSource } from './resourceVideoSource';

type VideoState = Extract<ReturnType<ResourceVideoSession['getState']>, { status:'available' }>;
export type ResourceStagePosition = { playhead:number; duration:number };
const RATES = [1, 1.5, 2, 0.5];

/** The recording stage shared by the moment and clip editors: the existing
 * telestrator with its toolbar above, transport and timeline below. */
export function ResourceVideoStage({ session, state, context, editable, active, focus, matchName, anchor, position,
  clip, readout, footer, onTime, onDuration }:{
  session:ResourceVideoSession; state:VideoState; context:DraftContext; editable:boolean; active:boolean; focus:boolean;
  matchName:string; anchor:number; position:MutableRefObject<ResourceStagePosition>;
  clip?:{ window:ClipWindow; onChange(window:ClipWindow):void };
  readout:(time:number) => ReactNode; footer?:(time:number, duration:number, video:HTMLVideoElement | null) => ReactNode;
  /** A settled frame: after the first seek to `anchor`, on pause and seek. */
  onTime?(seconds:number, video:HTMLVideoElement):void;
  /** The recording's length, once its metadata loads. */
  onDuration?(seconds:number):void;
}) {
  const value = state.value as ResourceVideoValue;
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const videoRef = useMemo(() => ({ current:video }), [video]);
  const playhead = useVideoPlayhead(videoRef);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const [rail, setRail] = useState<HTMLDivElement | null>(null);
  const envelope = useCanvasEnvelope();
  const anchored = useRef<HTMLVideoElement | null>(null);
  const settle = useRef(onTime); settle.current = onTime;
  const measured = useRef(onDuration); measured.current = onDuration;

  // The media grant is chosen once per recording: a saved version's exact pin,
  // else the recording itself. A save does not reload the playing video.
  const recordingKey = value.recording ? `${value.recording.recording_id}:${value.recording.recording_generation}` : '';
  const chosen = useRef<{ key:string; pin:ResourcePin | null }>({ key:'', pin:null });
  if (chosen.current.key !== recordingKey) {
    const saved = state.authority?.resource;
    const matches = saved?.availability === 'available' && saved.kind !== 'tactical_board' && value.recording
      && saved.payload.recording_id === value.recording.recording_id && saved.payload.recording_generation === value.recording.recording_generation;
    chosen.current = { key:recordingKey, pin:matches && state.origin ? { resource_id:state.origin.resource_id, revision_id:state.origin.revision_id } : null };
  }
  const source:ResourceVideoSource | null = value.recording ? { scope:context, recording:value.recording, pin:chosen.current.pin } : null;
  const sourceKey = resourceVideoSourceKey(source);
  const stableSource = useMemo(() => source, [sourceKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!video) return;
    const measure = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      setDuration(video.duration); measured.current?.(video.duration);
    };
    const seekAnchor = () => {
      measure();
      if (anchored.current === video) return;
      anchored.current = video;
      if (Math.abs(video.currentTime - anchor) < 0.01) settle.current?.(video.currentTime, video);
      else { video.currentTime = anchor; video.addEventListener('seeked', () => settle.current?.(video.currentTime, video), { once:true }); }
    };
    const settled = () => { if (anchored.current === video && video.paused) settle.current?.(video.currentTime, video); };
    if (video.readyState >= 1) seekAnchor();
    video.addEventListener('loadedmetadata', seekAnchor);
    video.addEventListener('durationchange', measure);
    video.addEventListener('pause', settled);
    video.addEventListener('seeked', settled);
    return () => {
      video.removeEventListener('loadedmetadata', seekAnchor);
      video.removeEventListener('durationchange', measure);
      video.removeEventListener('pause', settled);
      video.removeEventListener('seeked', settled);
    };
    // The anchor applies once per media element, never on later edits.
  }, [video]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (video) video.playbackRate = rate; }, [video, rate]);
  // The feedback composer above the page owns playback while it is open.
  useEffect(() => { if (!active) video?.pause(); }, [active, video]);
  const time = playhead.currentTime ?? 0;
  position.current = { playhead:time, duration };

  useEffect(() => {
    const keys = (event:KeyboardEvent) => {
      if (event.key !== ' ' || shortcutBlocked(event, active) || !video) return;
      event.preventDefault();
      if (video.paused) void video.play().catch(() => {}); else video.pause();
    };
    window.addEventListener('keydown', keys);
    return () => window.removeEventListener('keydown', keys);
  }, [video, active]);

  const setDrawing = (drawing:DrawingV2) => {
    const current = session.getState();
    if (current.status === 'available' && current.canEdit) session.change({ ...(current.value as ResourceVideoValue), drawing });
  };
  const seek = (seconds:number) => { if (video) video.currentTime = Math.max(0, Math.min(seconds, duration || seconds)); };
  const saved = state.authority?.resource;
  const savedMoment = saved?.availability === 'available' && saved.kind === 'moment' ? {
    resourceId:saved.resource_id, revisionId:saved.revision_id, recordingId:saved.payload.recording_id,
    recordingGeneration:saved.payload.recording_generation, seconds:saved.payload.timestamp_seconds, label:saved.display_label } : undefined;
  const slot = value.recording?.slot;

  return <div className="flex flex-col gap-3" data-testid="resource-video-stage">
    <div ref={setRail} hidden={!editable} aria-label="Drawing tools" role="group" data-testid="resource-drawing-tools"
      className="resource-editor-toolbar rounded-panel border border-rule bg-elevated p-1"/>
    <div ref={envelope.setRef} className="relative mx-auto w-full overflow-hidden rounded-panel bg-navy-900"
      style={{ aspectRatio:'16 / 9', maxHeight:focus ? 'calc(100dvh - 440px)' : 'min(62vh, calc(100dvh - 360px))',
        maxWidth:focus ? 'calc((100dvh - 440px) * 16 / 9)' : 'calc(min(62vh, calc(100dvh - 360px)) * 16 / 9)' }}>
      {envelope.envelopeWidth > 0 && <TelestratorCanvas resourceSource={stableSource} onResourceUnavailable={() => session.withholdContent()}
        drawing={value.drawing} onDrawingChange={setDrawing} width={envelope.envelopeWidth} videoRef={setVideo}
        inactive={!editable || !active} showDrawing={clip ? true : playhead.paused}
        toolRailSlot={controls => rail ? createPortal(<div className="flex flex-wrap items-center gap-3 px-2 py-1">{controls}</div>, rail) : null}/>}
    </div>
    <section className={`review-editor${clip ? ' is-clipping' : ''}`} data-testid="resource-editor-dock" data-focus={focus ? 'true' : 'false'}
      aria-label={clip ? 'Playback and clip range' : 'Playback and moment'}>
      <div className="editor-top-row">
        <div className="editor-playback">
          <FrameStepper videoRef={videoRef}/>
          <div className="editor-time" data-testid="resource-editor-time">
            <strong>{formatEditorTime(time)}</strong><span>/ {formatPlayhead(duration || null)}</span>
          </div>
          <button type="button" className="editor-button editor-rate" aria-label="Change playback speed" title="Change playback speed"
            onClick={() => setRate(current => RATES[(RATES.indexOf(current) + 1) % RATES.length])}>{rate}×</button>
        </div>
        <div className="editor-authoring">{readout(time)}</div>
      </div>
      <ClipTrack filmstrip={false} canTrim={editable} resourceSource={stableSource} onResourceUnavailable={() => session.withholdContent()}
        resourceMoment={clip ? undefined : savedMoment} duration={duration} playhead={time} onSeek={seek}
        clipWindow={clip?.window ?? null} onClipWindowChange={next => clip?.onChange(next)} inClipMode={!!clip}
        notes={[]} clips={[]} activeNoteId={null} activeClipId={null} onJumpToNote={() => {}} onOpenClip={() => {}}
        matchName={matchName} slotName={slot ? SLOT_LABELS[slot] : ''} matchId={value.recording?.match_id} slot={slot}/>
      {footer?.(time, duration, video)}
    </section>
  </div>;
}
